import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform, useReducedMotion } from "framer-motion";
import { Boxes, Megaphone, Code2, Cpu, ArrowRight, ArrowUpRight, Check, Globe2, Users, Clock, LifeBuoy, Wallet, CalendarCheck, HeartHandshake, Factory, HeartPulse, ShoppingCart, Landmark, Zap, GraduationCap } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { LogoAnimation } from "../components/LogoAnimation";
import { Marquee } from "../components/Marquee";
import { ContactForm } from "../components/ContactForm";
import { PILLARS, COMMITMENTS, REGIONS, REGION_FLAGS, INDUSTRIES } from "../data/services";

const PILLAR_ICONS = { Boxes, Megaphone, Code2, Cpu };
const COMMITMENT_ICONS = [Users, Clock, LifeBuoy, Wallet, CalendarCheck, HeartHandshake];
const INDUSTRY_ICONS = { Factory, HeartPulse, ShoppingCart, Landmark, Zap, GraduationCap };

const APPROACH = [
    { step: "01", title: "Understand the business need", desc: "Every engagement starts with listening — your goals, your systems, your constraints." },
    { step: "02", title: "Plan the right solution", desc: "A practical, right-sized plan: technology, timeline and cost agreed before work begins." },
    { step: "03", title: "Deliver with care", desc: "Built, integrated and tested carefully, with visible progress all the way through." },
    { step: "04", title: "Support the client after launch", desc: "Go-live is a milestone, not a goodbye. We stay available long after launch." },
];

const MaskedLine = ({ children, delay }) => (
    <span className="block overflow-hidden pb-1">
        <motion.span
            className="block"
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
        >
            {children}
        </motion.span>
    </span>
);

const Hero = () => {
    const reduce = useReducedMotion();
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const sx = useSpring(mx, { stiffness: 60, damping: 18 });
    const sy = useSpring(my, { stiffness: 60, damping: 18 });
    const logoX = useTransform(sx, (v) => v * 16);
    const logoY = useTransform(sy, (v) => v * 12);
    const orbX = useTransform(sx, (v) => v * -30);
    const orbY = useTransform(sy, (v) => v * -22);

    const onMove = (e) => {
        if (reduce) return;
        const r = e.currentTarget.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
    };

    return (
        <section
            className="bg-grid relative overflow-hidden"
            onMouseMove={onMove}
            data-testid="hero-section"
        >
            <div
                className="pointer-events-none absolute inset-0"
                style={{
                    background:
                        "radial-gradient(900px 500px at 15% 10%, rgba(0,102,255,0.14), transparent 60%), radial-gradient(800px 480px at 85% 20%, rgba(230,0,126,0.10), transparent 60%), radial-gradient(700px 520px at 50% 100%, rgba(112,0,255,0.12), transparent 65%)",
                }}
            />
            <motion.div
                className="float-slow pointer-events-none absolute -left-24 top-24 h-72 w-72 rounded-full"
                style={{ x: orbX, y: orbY, background: "radial-gradient(circle, rgba(0,240,255,0.12), transparent 65%)", filter: "blur(30px)" }}
            />
            <motion.div
                className="float-slow pointer-events-none absolute -right-16 bottom-10 h-80 w-80 rounded-full"
                style={{ x: logoX, y: logoY, background: "radial-gradient(circle, rgba(230,0,126,0.12), transparent 65%)", filter: "blur(34px)", animationDelay: "1.4s" }}
            />

            <div className="container-x relative grid items-center gap-14 py-20 lg:grid-cols-2 lg:gap-10 lg:py-28">
                <div>
                    <motion.p
                        className="eyebrow text-cyan-300"
                        initial={reduce ? {} : { opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8 }}
                        data-testid="hero-eyebrow"
                    >
                        Innovated Global IT Solutions Pvt. Ltd.
                    </motion.p>

                    <h1 className="mt-6 text-4xl font-bold leading-[1.06] tracking-tight sm:text-5xl lg:text-6xl" data-testid="hero-headline">
                        <MaskedLine delay={0.15}>Technology that moves</MaskedLine>
                        <MaskedLine delay={0.3}>
                            your business <span className="text-gradient-brand">forward.</span>
                        </MaskedLine>
                    </h1>

                    <motion.p
                        className="mt-7 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg"
                        initial={reduce ? {} : { opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.55, ease: [0.16, 1, 0.3, 1] }}
                        data-testid="hero-subtext"
                    >
                        From Infor systems and custom software to digital marketing and AI, we bring the right
                        expertise together to help your business work smarter and grow with confidence.
                    </motion.p>

                    <motion.div
                        className="mt-10 flex flex-wrap items-center gap-4"
                        initial={reduce ? {} : { opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
                    >
                        <button
                            type="button"
                            className="btn-primary"
                            data-testid="hero-explore-services-button"
                            onClick={() => {
                                const el = document.getElementById("services");
                                if (el && window.__lenis) window.__lenis.scrollTo(el, { offset: -90 });
                                else if (el) el.scrollIntoView({ behavior: "smooth" });
                            }}
                        >
                            Explore Our Services
                            <ArrowRight size={16} aria-hidden="true" />
                        </button>
                        <Link to="/contact" className="btn-ghost" data-testid="hero-talk-team-button">
                            Talk to Our Team
                            <ArrowUpRight size={16} aria-hidden="true" />
                        </Link>
                    </motion.div>

                    <motion.div
                        className="mt-10"
                        initial={reduce ? {} : { opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 0.9 }}
                        data-testid="hero-regions-line"
                    >
                        <span className="text-xs tracking-wider text-slate-500">SUPPORTING CLIENTS ACROSS</span>
                        <span className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
                            {["INDIA", "US", "MIDDLE EAST", "UK", "EUROPE", "AUSTRALIA"].map((r, i) => (
                                <span key={r} className="hero-region" style={{ animationDelay: `${i * 0.55}s` }}>
                                    {r}
                                </span>
                            ))}
                        </span>
                    </motion.div>
                </div>

                <motion.div
                    className="relative mx-auto w-full max-w-[520px]"
                    style={reduce ? {} : { x: logoX, y: logoY }}
                    initial={reduce ? {} : { opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1.1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                >
                    <LogoAnimation />
                </motion.div>
            </div>
        </section>
    );
};

const ServicesSection = () => (
    <section id="services" className="py-20 lg:py-28" data-testid="services-section">
        <div className="container-x">
            <Reveal>
                <p className="eyebrow text-cyan-300">Four pillars, one partner</p>
                <h2 className="mt-4 max-w-2xl text-2xl font-semibold tracking-tight sm:text-3xl lg:text-4xl">
                    Everything your business needs to run, reach and grow.
                </h2>
                <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-400">
                    Each petal of our flower is a practice of its own — deep expertise in one area, connected at
                    the center so your systems, marketing and AI work as one.
                </p>
            </Reveal>

            <div className="services-grid mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
                {PILLARS.map((p, i) => {
                    const Icon = PILLAR_ICONS[p.icon];
                    return (
                        <Reveal key={p.slug} delay={i * 0.08}>
                            <Link
                                to={`/services/${p.slug}`}
                                data-testid={`service-card-${p.slug}`}
                                className="service-card group glass flex h-full flex-col rounded-3xl p-6 sm:p-7"
                                style={{ "--accent": p.accent, "--accent-soft": p.accentSoft }}
                            >
                                <span
                                    className="card-icon inline-flex h-12 w-12 items-center justify-center rounded-2xl transition-transform duration-300 group-hover:scale-110"
                                    style={{ background: p.accentSoft, color: p.accent, animationDelay: `${i * 0.7}s` }}
                                >
                                    <Icon size={22} aria-hidden="true" />
                                </span>
                                <h3 className="mt-5 text-lg font-semibold sm:text-xl">{p.name}</h3>
                                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-400">{p.summary}</p>
                                <ul className="mt-5 space-y-2">
                                    {p.capabilities.slice(0, 3).map((c) => (
                                        <li key={c} className="flex items-center gap-2 text-xs text-slate-400">
                                            <Check size={13} style={{ color: p.accent }} aria-hidden="true" />
                                            {c}
                                        </li>
                                    ))}
                                </ul>
                                <span
                                    className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold transition-colors duration-200"
                                    style={{ color: p.accent }}
                                >
                                    Learn more
                                    <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                                </span>
                                <span
                                    className="pointer-events-none mt-5 block h-[3px] w-full rounded-full opacity-40 transition-opacity duration-300 group-hover:opacity-100"
                                    style={{ background: `linear-gradient(90deg, ${p.accent}, transparent)` }}
                                />
                            </Link>
                        </Reveal>
                    );
                })}
            </div>
        </div>
    </section>
);

const ApproachSection = () => (
    <section className="border-y border-white/5 py-20 lg:py-28" style={{ background: "linear-gradient(135deg, rgba(0,102,255,0.07), rgba(19,26,48,0.55) 42%, rgba(112,0,255,0.07))" }} data-testid="approach-section">
        <div className="container-x">
            <div className="grid gap-12 lg:grid-cols-5">
                <Reveal className="lg:col-span-2">
                    <p className="eyebrow text-violet-400">How we work</p>
                    <h2 className="mt-4 text-2xl font-semibold tracking-tight sm:text-3xl lg:text-4xl">
                        A straightforward approach, from first call to long after launch.
                    </h2>
                    <p className="mt-4 text-base leading-relaxed text-slate-400">
                        We help businesses connect their systems, strengthen their digital presence, build software
                        and apply AI — with a way of working that keeps things clear at every step.
                    </p>
                    <Link to="/about" className="btn-ghost mt-8" data-testid="approach-about-link">
                        More about us
                        <ArrowUpRight size={16} aria-hidden="true" />
                    </Link>
                </Reveal>

                <div className="focus-grid grid gap-5 sm:grid-cols-2 lg:col-span-3">
                    {APPROACH.map((s, i) => (
                        <Reveal key={s.step} delay={i * 0.08}>
                            <div
                                className="focus-card glass h-full rounded-3xl p-6 sm:p-7"
                                style={{ "--accent": ["#0066FF", "#E6007E", "#7000FF", "#00F0FF"][i % 4], "--accent-soft": ["#0066FF", "#E6007E", "#7000FF", "#00F0FF"][i % 4] + "24" }}
                                data-testid={`approach-step-${s.step}`}
                            >
                                <span
                                    className="text-4xl font-bold"
                                    style={{
                                        fontFamily: "Outfit, sans-serif",
                                        color: "transparent",
                                        WebkitTextStroke: "1.5px rgba(148,163,184,0.45)",
                                    }}
                                >
                                    {s.step}
                                </span>
                                <h3 className="mt-4 text-lg font-semibold">{s.title}</h3>
                                <p className="mt-2 text-sm leading-relaxed text-slate-400">{s.desc}</p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </div>
        </div>
    </section>
);

const CommitmentsSection = () => {
    const reduce = useReducedMotion();
    const [activeFlag, setActiveFlag] = useState(-1);
    useEffect(() => {
        if (reduce) return undefined;
        let idx = -1;
        let showing = false;
        let timer;
        const tick = () => {
            if (!showing) {
                idx = (idx + 1) % REGIONS.length;
                setActiveFlag(idx);
                showing = true;
                timer = setTimeout(tick, 2000);
            } else {
                setActiveFlag(-1);
                showing = false;
                timer = setTimeout(tick, 1000);
            }
        };
        timer = setTimeout(tick, 1200);
        return () => clearTimeout(timer);
    }, [reduce]);
    return (
    <section className="py-20 lg:py-28" data-testid="commitments-section">
        <div className="container-x">
            <Reveal className="text-center">
                <p className="eyebrow text-fuchsia-400">Our commitments</p>
                <h2 className="mx-auto mt-4 max-w-2xl text-2xl font-semibold tracking-tight sm:text-3xl lg:text-4xl">
                    What you can expect when you work with us.
                </h2>
            </Reveal>

            <div className="focus-grid mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {COMMITMENTS.map((c, i) => {
                    const Icon = COMMITMENT_ICONS[i];
                    const accent = ["#0066FF", "#E6007E", "#7000FF", "#00F0FF"][i % 4];
                    return (
                        <Reveal key={c.title} delay={i * 0.06}>
                            <div
                                className="focus-card glass flex h-full gap-4 rounded-3xl p-6"
                                style={{ "--accent": accent, "--accent-soft": `${accent}24` }}
                                data-testid={`commitment-${i}`}
                            >
                                <span
                                    className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl"
                                    style={{ background: `${accent}22`, color: accent }}
                                >
                                    <Icon size={20} aria-hidden="true" />
                                </span>
                                <div>
                                    <h3 className="text-base font-semibold">{c.title}</h3>
                                    <p className="mt-1.5 text-sm leading-relaxed text-slate-400">{c.desc}</p>
                                </div>
                            </div>
                        </Reveal>
                    );
                })}
            </div>

            <Reveal className="mt-14">
                <div className="glass flex flex-col items-center gap-6 rounded-3xl p-8 sm:p-10" data-testid="global-regions-panel">
                    <div className="flex items-center gap-3">
                        <Globe2 size={22} className="text-cyan-300" aria-hidden="true" />
                        <h3 className="text-lg font-semibold sm:text-xl">Global support, local understanding</h3>
                    </div>
                    <p className="max-w-2xl text-center text-sm leading-relaxed text-slate-400">
                        We support clients across six regions — delivering remotely with clear communication and
                        planned overlap with your working hours.
                    </p>
                    <ul className="flex flex-wrap justify-center gap-3">
                        {REGIONS.map((r, i) => (
                            <li
                                key={r}
                                className="region-chip rounded-full border border-white/10 px-4 py-2 text-xs font-medium tracking-wide text-slate-300"
                                style={{ background: "rgba(255,255,255,0.03)" }}
                                data-testid={`region-chip-${i}`}
                            >
                                <span className="region-name">{r}</span>
                                <span className={`region-flag ${activeFlag === i ? "flag-on" : ""}`} aria-hidden="true">{REGION_FLAGS[r]}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </Reveal>
        </div>
    </section>
    );
};

const IndustriesSection = () => (
    <section className="border-y border-white/5 py-20 lg:py-28" style={{ background: "linear-gradient(135deg, rgba(0,102,255,0.07), rgba(19,26,48,0.55) 42%, rgba(112,0,255,0.07))" }} data-testid="industries-section">
        <div className="container-x">
            <Reveal>
                <p className="eyebrow text-blue-400">Who we support</p>
                <h2 className="mt-4 max-w-2xl text-2xl font-semibold tracking-tight sm:text-3xl lg:text-4xl">
                    Built for organizations with different business needs.
                </h2>
                <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-400">
                    Every organization runs on its own mix of systems, customers and constraints. Our four service
                    pillars adapt to that mix — whatever your sector looks like.
                </p>
            </Reveal>

            <div className="focus-grid mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {INDUSTRIES.map((ind, i) => {
                    const Icon = INDUSTRY_ICONS[ind.icon];
                    const accent = ["#0066FF", "#E6007E", "#7000FF", "#00F0FF"][i % 4];
                    return (
                        <Reveal key={ind.name} delay={i * 0.05}>
                            <div
                                className="focus-card glass group h-full rounded-3xl p-6"
                                style={{ "--accent": accent, "--accent-soft": `${accent}24` }}
                                data-testid={`industry-card-${i}`}
                            >
                                <Icon size={22} className="text-slate-300 transition-colors duration-300 group-hover:text-cyan-300" aria-hidden="true" />
                                <h3 className="mt-4 text-base font-semibold">{ind.name}</h3>
                                <p className="mt-2 text-sm leading-relaxed text-slate-400">{ind.desc}</p>
                            </div>
                        </Reveal>
                    );
                })}
            </div>
        </div>
    </section>
);

const ContactSection = () => (
    <section id="contact" className="relative overflow-hidden py-20 lg:py-28" data-testid="contact-section">
        <div
            className="pointer-events-none absolute inset-0"
            style={{ background: "radial-gradient(800px 400px at 80% 20%, rgba(112,0,255,0.12), transparent 60%), radial-gradient(700px 400px at 10% 90%, rgba(0,240,255,0.08), transparent 60%)" }}
        />
        <div className="container-x relative grid gap-12 lg:grid-cols-2 lg:gap-16">
            <Reveal>
                <p className="eyebrow text-cyan-300">Let's talk</p>
                <h2 className="mt-4 text-2xl font-semibold tracking-tight sm:text-3xl lg:text-4xl">
                    Tell us where your business wants to go.
                </h2>
                <p className="mt-4 max-w-md text-base leading-relaxed text-slate-400">
                    Share a few details about what you need — a system, a campaign, an application, or an honest
                    conversation about AI — and our team will get back to you.
                </p>
                <ul className="mt-8 space-y-3">
                    {[
                        "A real person reads every enquiry",
                        "Clear next steps, no obligation",
                        "Reachable on phone call, video call, text and email",
                    ].map((t) => (
                        <li key={t} className="flex items-center gap-3 text-sm text-slate-300">
                            <Check size={16} className="text-cyan-300" aria-hidden="true" />
                            {t}
                        </li>
                    ))}
                </ul>
            </Reveal>
            <Reveal delay={0.1}>
                <ContactForm />
            </Reveal>
        </div>
    </section>
);

export default function Home() {
    return (
        <>
            <Seo
                title=""
                description="Infor WMS and ION consulting, custom software development, digital marketing and AI/ML solutions from Innovated Global IT Solutions. Ideas. Innovation. Impact."
            />
            <Hero />
            <Marquee />
            <ServicesSection />
            <ApproachSection />
            <CommitmentsSection />
            <IndustriesSection />
            <ContactSection />
        </>
    );
}
